if (!customElements.get('slynara-sticky-atc')) {
  customElements.define(
    'slynara-sticky-atc',
    class SlynaraStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.mainButton = this.getMainButton();
        this.form = this.querySelector('form');
        this.variantInput = this.querySelector('input[name="id"]');
        this.quantityInput = this.querySelector('[data-slynara-sticky-quantity]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideWhenUnavailable = this.hasAttribute('data-hide-when-unavailable');
        this.variantAvailable = !this.submitButton?.disabled;

        if (!this.productInfo || !this.mainButton || !this.form || !this.variantInput || !this.submitButton) return;

        this.handleSubmitCapture = this.syncQuantity.bind(this);
        this.handleProductChange = this.handleProductChange.bind(this);
        this.form.addEventListener('submit', this.handleSubmitCapture, true);
        this.productInfo.addEventListener('change', this.handleProductChange);
        this.visibilityObserver = new IntersectionObserver(this.handleIntersection.bind(this));
        this.visibilityObserver.observe(this.mainButton);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
        this.syncFromMainProduct();
        customElements.whenDefined('media-gallery').then(() => {
          if (this.isConnected) this.syncFromMainProduct();
        });
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmitCapture, true);
        this.productInfo?.removeEventListener('change', this.handleProductChange);
        this.visibilityObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      }

      handleIntersection(entries) {
        const entry = entries[0];
        this.hasPassedMainButton = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        this.updateVisibility();
      }

      updateVisibility() {
        const visible = Boolean(this.hasPassedMainButton && !(this.hideWhenUnavailable && !this.variantAvailable));
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
      }

      handleVariantChange({ data }) {
        if (String(data.sectionId) !== String(this.sectionId)) return;

        this.syncFromMainProduct(data.variant);
      }

      handleProductChange(event) {
        const mainForm = event.target.closest('form');
        if (event.target.name !== 'id' || mainForm?.getAttribute('id') !== `product-form-${this.sectionId}`) return;

        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = undefined;
          this.syncFromMainProduct();
        });
      }

      syncFromMainProduct(eventVariant) {
        const mainButton = this.getMainButton();
        const mainVariantInput = this.productInfo.querySelector(`#product-form-${this.sectionId} input[name="id"]`);
        if (!mainButton || !mainVariantInput) return;

        const variantText = this.querySelector('[data-slynara-sticky-variant]');
        const price = this.querySelector('[data-slynara-sticky-price]');
        const media = this.querySelector('[data-slynara-sticky-media]');
        const availability = this.querySelector('[data-slynara-sticky-availability]');
        const stickyText = this.submitButton.querySelector('span');
        const mainPrice = this.productInfo.querySelector(`#price-${this.sectionId}`);
        const mainVariantId = mainVariantInput.value;
        const selectedVariant = this.getSelectedVariant();
        const currentVariant = selectedVariant?.id?.toString() === mainVariantId ? selectedVariant : eventVariant;
        const currentVariantIsAuthoritative = currentVariant?.id?.toString() === mainVariantId;
        const variantPicker = this.getVariantPicker();
        const checkedOptions = [...(variantPicker?.querySelectorAll('input[type="radio"]:checked') || [])];
        const selectedControls = checkedOptions.length
          ? checkedOptions
          : [...(variantPicker?.querySelectorAll('select') || [])];
        const selectedOptions = currentVariantIsAuthoritative
          ? currentVariant.options
          : selectedControls.map((control) => control.value).filter(Boolean);
        const available = Boolean(currentVariantIsAuthoritative && currentVariant.available && !mainButton.disabled);
        const mainText = mainButton.querySelector('span:not(.sold-out-message)')?.textContent?.trim();

        this.variantInput.value = currentVariantIsAuthoritative ? mainVariantId : '';
        this.variantAvailable = available;
        this.variantInput.disabled = !available;
        this.submitButton.toggleAttribute('disabled', !available);
        if (variantText) {
          const options = selectedOptions?.filter((option) => option !== 'Default Title') || [];
          variantText.textContent = options.join(' / ');
          variantText.toggleAttribute('hidden', options.length === 0);
        }
        if (mainText && stickyText) stickyText.textContent = mainText;
        if (availability) availability.textContent = available ? this.dataset.availableLabel : mainText || window.variantStrings.unavailable;
        if (mainPrice && price && currentVariantIsAuthoritative) price.innerHTML = mainPrice.innerHTML;
        if (price) price.classList.toggle('hidden', !currentVariantIsAuthoritative || mainPrice?.classList.contains('hidden'));
        const preferredMediaImage = this.getPreferredMediaImage(currentVariant);
        if (preferredMediaImage && media) {
          const image = preferredMediaImage.cloneNode(true);
          image.className = 'slynara-sticky-atc__image';
          image.loading = 'lazy';
          image.sizes = '64px';
          media.replaceChildren(image);
          media.classList.remove('slynara-sticky-atc__media--empty');
        }
        this.updateVisibility();
      }

      getPreferredMediaImage(currentVariant) {
        const gallery = this.productInfo.querySelector('media-gallery');
        if (!gallery) return null;

        const imageSlides = [...gallery.querySelectorAll('.product__media-item[data-media-type="image"]')];
        if (gallery.colorMedia) {
          const firstVisibleImage = imageSlides.find(
            (slide) => !slide.hidden && slide.style.display !== 'none'
          );
          if (firstVisibleImage) return firstVisibleImage.querySelector('img');
        }

        const featuredMediaId = currentVariant?.featured_media?.id?.toString();
        if (featuredMediaId) {
          const featuredMediaSlide = imageSlides.find(
            (slide) => slide.dataset.mediaId?.split('-').pop() === featuredMediaId
          );
          if (featuredMediaSlide) return featuredMediaSlide.querySelector('img');
        }

        return imageSlides.find((slide) => !slide.hidden && slide.style.display !== 'none')?.querySelector('img') ||
          imageSlides[0]?.querySelector('img') ||
          null;
      }

      getMainButton() {
        return this.productInfo?.querySelector(`#${this.dataset.mainSubmitId}`);
      }

      getSelectedVariant() {
        try {
          return JSON.parse(this.getVariantPicker()?.querySelector('[data-selected-variant]')?.textContent || 'null');
        } catch {
          return null;
        }
      }

      getVariantPicker() {
        return [...this.productInfo.querySelectorAll('variant-selects')].find((picker) => picker.style.display !== 'none');
      }

      syncQuantity() {
        const mainQuantity = document.querySelector(`#Quantity-${this.sectionId}`);
        if (mainQuantity && this.quantityInput) this.quantityInput.value = mainQuantity.value;
      }
    }
  );
}
